/* Client-side types for the AI21 identity service (the same backend studio
 * uses), plus the raw server shapes and the mappers between them. The server
 * speaks snake_case; everything past HttpIdentityClient is camelCase. */

export type WorkspaceType = "private" | "personal" | "public";

export interface Workspace {
  id: string;
  name: string;
  type: WorkspaceType;
  /** Accent color from the workspace's ui_metadata (hex from the backend). */
  color?: string;
  organizationId: string;
  ownerId: string;
}

export interface NewWorkspace {
  name: string;
  type?: WorkspaceType;
  color?: string;
}

export type OrgRole = "ADMIN" | "MEMBER";

export interface OrgMember {
  id: string;
  email: string;
  name: string;
  /** Absent while the invite is pending. */
  role?: OrgRole;
  /** Derived: no role yet ⇒ the invite hasn't been accepted. */
  pending: boolean;
  addedOn: string;
}

export interface Organization {
  id: string;
  name: string;
}

/** A pending organization invitation, resolved from an invitation id. The
 *  identity service validates that the signed-in user's email matches the
 *  invite before returning it. */
export interface Invite {
  invitationId: string;
  /** The organization the invite grants membership to. */
  organization: Organization;
}

export interface ApiKey {
  id: string;
  name: string;
  value: string;
  workspaceId: string;
  status: string;
  /** ISO date (yyyy-mm-dd); undefined ⇒ never expires. */
  expiresAt?: string;
}

/* ---- Server shapes (verbatim from the identity API) ---- */

export interface ServerWorkspace {
  id: string;
  name: string;
  workspace_type: WorkspaceType;
  ui_metadata: { color: string };
  icon: string;
  owner_id: string;
  organization_id: string;
}

export interface ServerOrgUser {
  id: string;
  email: string;
  name: string;
  role?: OrgRole;
  added_on: string;
}

export interface ServerApiKey {
  key_id: string;
  key_value: string;
  key_name: string;
  workspace_id: string;
  status: string;
  expiration_date?: string | null;
}

/* ---- Mappers ---- */

export function workspaceFromServer(w: ServerWorkspace): Workspace {
  return {
    id: w.id,
    name: w.name,
    type: w.workspace_type,
    color: w.ui_metadata?.color,
    organizationId: w.organization_id,
    ownerId: w.owner_id,
  };
}

export function memberFromServer(u: ServerOrgUser): OrgMember {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    role: u.role,
    pending: u.role === undefined,
    addedOn: u.added_on,
  };
}

export function apiKeyFromServer(k: ServerApiKey): ApiKey {
  return {
    id: k.key_id,
    value: k.key_value,
    name: k.key_name || "API key",
    workspaceId: k.workspace_id,
    status: k.status,
    // The server returns a datetime; the UI only cares about the day.
    expiresAt: k.expiration_date ? k.expiration_date.split("T")[0] : undefined,
  };
}
